import {
  FlattenedSaleRow,
  SaleAnswerRow,
  SelectedSaleAnswer,
  SaleColumnDef,
} from './types/SalesTypes';

export const SALES_ANSWER_ROW_HEIGHT = 36;
export const SALES_FORM_ANSWER_ROW_HEIGHT = 48;
export const SALES_EPISODE_ROW_HEIGHT = 44;
/** @deprecated use SALES_ANSWER_ROW_HEIGHT */
export const SALES_ROW_HEIGHT = SALES_ANSWER_ROW_HEIGHT;
export const SALES_OVERSCAN = 8;
/** Extra scroll space so the last row is fully visible without bottom-edge jitter. */
export const SALES_SCROLL_BOTTOM_PAD = SALES_ANSWER_ROW_HEIGHT;

export const MODAL_SALE_COLUMNS: SaleColumnDef[] = [
  { id: 'episode', label: 'Ep #', minWidth: 45, defaultWidth: 65 },
  { id: 'node', label: 'Node', minWidth: 60, defaultWidth: 180 },
  { id: 'stepText', label: 'Step Text', minWidth: 85, defaultWidth: 160 },
  { id: 'answerText', label: 'Answer Text', minWidth: 107, defaultWidth: 230 },
  { id: 'answerId', label: 'Answer ID', minWidth: 55, defaultWidth: 95 },
  { id: 'answerIndex', label: 'Index', minWidth: 40, defaultWidth: 60 },
  { id: 'currencySetting', label: 'Currency Setting', minWidth: 85, defaultWidth: 100 },
  { id: 'price', label: 'Price', minWidth: 35, defaultWidth: 60 },
  { id: 'sale', label: 'Sale', minWidth: 50, defaultWidth: 60 },
];

export const FORM_SALE_COLUMNS: SaleColumnDef[] = [
  { id: 'episode', label: 'Ep #', minWidth: 45, defaultWidth: 65 },
  { id: 'node', label: 'Node', minWidth: 60, defaultWidth: 180 },
  { id: 'stepText', label: 'Step Text', minWidth: 85, defaultWidth: 160 },
  { id: 'answerText', label: 'Answer Text', minWidth: 107, defaultWidth: 230 },
  { id: 'answerId', label: 'Answer ID', minWidth: 55, defaultWidth: 95 },
  { id: 'answerIndex', label: 'Index', minWidth: 30, defaultWidth: 55 },
  { id: 'currencySetting', label: 'Currency Setting', minWidth: 85, defaultWidth: 100 },
  { id: 'price', label: 'Price', minWidth: 35, defaultWidth: 60 },
  { id: 'discount', label: 'Discount %', minWidth: 80, defaultWidth: 105 },
  { id: 'salePrice', label: 'Sale Price', minWidth: 51, defaultWidth: 88 },
];

export function defaultColumnWidths(columns: SaleColumnDef[]): Record<string, number> {
  return columns.reduce<Record<string, number>>((acc, col) => {
    acc[col.id] = col.defaultWidth;
    return acc;
  }, {});
}

export function saleAnswerKey(
  answer: Pick<SaleAnswerRow, 'episodeUuid' | 'nodeTitle' | 'answerIndex'>,
): string {
  return `${answer.episodeUuid}|${answer.nodeTitle}|${answer.answerIndex}`;
}

export function isSelectableSaleAnswer(answer: SaleAnswerRow): boolean {
  return answer.answerIndex != null;
}

export function episodeSubheaderLabel(row: {
  isBonus: boolean;
  episode: number | null;
  episodeTitle: string;
  episodeId: number;
}): string {
  if (row.isBonus) {
    return `[${row.episodeId}] Bonus : ${row.episodeTitle}`;
  }
  return `[${row.episodeId}] Ep. ${row.episode} : ${row.episodeTitle}`;
}

export function episodeCellLabel(answer: SaleAnswerRow): string {
  if (answer.isBonus) {
    return 'Bonus';
  }
  return answer.episode != null ? String(answer.episode) : '';
}

export function getRowHeight(row: FlattenedSaleRow, answerRowHeight = SALES_ANSWER_ROW_HEIGHT): number {
  return row.type === 'episode' ? SALES_EPISODE_ROW_HEIGHT : answerRowHeight;
}

export function computeRowLayout(rows: FlattenedSaleRow[], answerRowHeight = SALES_ANSWER_ROW_HEIGHT) {
  const heights: number[] = [];
  const offsets: number[] = [];
  let totalHeight = 0;

  for (const row of rows) {
    offsets.push(totalHeight);
    const height = getRowHeight(row, answerRowHeight);
    heights.push(height);
    totalHeight += height;
  }

  return { heights, offsets, totalHeight };
}

export function findRowIndexAtOffset(offsets: number[], scrollTop: number): number {
  let lo = 0;
  let hi = offsets.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (offsets[mid] <= scrollTop) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
}

export function buildEpisodeSelectableKeyMap(answers: SaleAnswerRow[]): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const answer of answers) {
    if (!isSelectableSaleAnswer(answer)) {
      continue;
    }
    const key = saleAnswerKey(answer);
    const list = map.get(answer.episodeUuid) ?? [];
    list.push(key);
    map.set(answer.episodeUuid, list);
  }
  return map;
}

export function selectionStateForKeys(
  keys: string[],
  selectedKeys: Set<string>,
): { checked: boolean; indeterminate: boolean } {
  if (keys.length === 0) {
    return { checked: false, indeterminate: false };
  }
  let selectedCount = 0;
  for (const key of keys) {
    if (selectedKeys.has(key)) {
      selectedCount += 1;
    }
  }
  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }
  if (selectedCount === keys.length) {
    return { checked: true, indeterminate: false };
  }
  return { checked: false, indeterminate: true };
}

export function computeSalePrice(price: number, discountPercent: number): number {
  const clamped = Math.min(100, Math.max(0, discountPercent));
  return Math.ceil(price * (1 - clamped / 100));
}

export function clampDiscount(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function buildFlattenedRows(answers: SaleAnswerRow[]): FlattenedSaleRow[] {
  const rows: FlattenedSaleRow[] = [];
  let lastEpisodeUuid: string | null = null;

  for (const answer of answers) {
    if (answer.episodeUuid !== lastEpisodeUuid) {
      rows.push({
        type: 'episode',
        key: `ep-${answer.episodeUuid}`,
        episodeUuid: answer.episodeUuid,
        episode: answer.episode,
        episodeTitle: answer.episodeTitle,
        episodeId: answer.episodeId,
        isBonus: answer.isBonus,
      });
      lastEpisodeUuid = answer.episodeUuid;
    }
    rows.push({
      type: 'answer',
      key: isSelectableSaleAnswer(answer) ? saleAnswerKey(answer) : `missing-${answer.episodeUuid}-${answer.nodeTitle}-${answer.price}-${rows.length}`,
      answer,
    });
  }

  return rows;
}

export function filterSaleAnswersForModal(answers: SaleAnswerRow[], showUnreleased: boolean): SaleAnswerRow[] {
  if (showUnreleased) {
    return answers;
  }
  return answers.filter((answer) => answer.isReleased);
}

export function getSelectableKeys(answers: SaleAnswerRow[]): string[] {
  return answers
    .filter(isSelectableSaleAnswer)
    .map((answer) => saleAnswerKey(answer));
}

export function mergeSelectionFromModal(
  answers: SaleAnswerRow[],
  selectedKeys: Set<string>,
  existingByKey: Map<string, SelectedSaleAnswer>,
): SelectedSaleAnswer[] {
  const selected: SelectedSaleAnswer[] = [];

  for (const answer of answers) {
    if (!isSelectableSaleAnswer(answer)) {
      continue;
    }
    const key = saleAnswerKey(answer);
    if (!selectedKeys.has(key)) {
      continue;
    }
    const existing = existingByKey.get(key);
    selected.push({
      ...answer,
      key,
      answerIndex: answer.answerIndex as number,
      discountPercent: existing?.discountPercent ?? 0,
    });
  }

  return selected;
}

export function selectedToKeySet(selected: SelectedSaleAnswer[]): Set<string> {
  return new Set(selected.map((row) => row.key));
}

export function selectedToDiscountMap(selected: SelectedSaleAnswer[]): Map<string, SelectedSaleAnswer> {
  return new Map(selected.map((row) => [row.key, row]));
}

export const SALE_EXPORT_CSV_HEADERS = [
  { label: 'title', key: 'title' },
  { label: 'chapter', key: 'chapter' },
  { label: 'episode title', key: 'episodeTitle' },
  { label: 'node', key: 'node' },
  { label: 'stepText', key: 'stepText' },
  { label: 'answerText', key: 'answerText' },
  { label: 'answerPrice', key: 'answerPrice' },
  { label: 'answerId', key: 'answerId' },
  { label: 'answerIndex', key: 'answerIndex' },
  { label: 'episodeId', key: 'episodeId' },
  { label: 'currencySetting', key: 'CurrencySetting' },
  { label: 'hasSale', key: 'hasSale' },
] as const;

export function buildSaleExportRows(
  storyTitle: string,
  answers: SaleAnswerRow[],
): Array<Record<string, string | number>> {
  return answers.map((answer) => ({
    title: storyTitle,
    chapter: answer.isBonus ? 0 : (answer.episode ?? 0),
    episodeTitle: answer.episodeTitle,
    node: answer.nodeTitle,
    stepText: answer.stepText,
    answerText: answer.answerText,
    answerPrice: answer.price,
    answerId: answer.answerId,
    answerIndex: answer.answerIndex == null ? '' : answer.answerIndex,
    episodeId: answer.episodeId,
    CurrencySetting: answer.currencySetting,
    hasSale: '',
  }));
}

export function saleExportFilename(storyTitle: string): string {
  const safeTitle = (storyTitle || 'sale-answers').replace(/[^\w.-]+/g, '-').replace(/-+/g, '-');
  return `${safeTitle}-sale-answers.csv`;
}

export function isCsvFilename(filename: string): boolean {
  return filename.toLowerCase().endsWith('.csv');
}

function parseCsvRecords(text: string): string[][] {
  const src = text.replace(/^\uFEFF/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
  const commaCount = (firstLine.match(/,/g) || []).length;
  const tabCount = (firstLine.match(/\t/g) || []).length;
  const semicolonCount = (firstLine.match(/;/g) || []).length;
  let delimiter = ',';
  if (tabCount > commaCount && tabCount >= semicolonCount) {
    delimiter = '\t';
  } else if (semicolonCount > commaCount) {
    delimiter = ';';
  }

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < src.length; i += 1) {
    const char = src[i];
    if (inQuotes) {
      if (char === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      field = '';
      if (row.some((cell) => cell.trim() !== '')) {
        rows.push(row);
      }
      row = [];
    } else if (char !== '\r') {
      field += char;
    }
  }

  if (field.length > 0 || row.length > 0) {
    row.push(field);
    if (row.some((cell) => cell.trim() !== '')) {
      rows.push(row);
    }
  }

  return rows;
}

export function parseSaleImportCsv(text: string): {
  error?: string;
  markedAnswerIds: number[];
  invalidAnswerIds: string[];
} {
  const records = parseCsvRecords(text);
  if (records.length === 0) {
    return {
      error: 'The CSV file is empty.',
      markedAnswerIds: [],
      invalidAnswerIds: [],
    };
  }

  const headers = records[0].map((header) => header.trim().toLowerCase());
  const answerIdIndex = headers.indexOf('answerid');
  const hasSaleIndex = headers.indexOf('hassale');
  if (answerIdIndex < 0 || hasSaleIndex < 0) {
    return {
      error: 'The CSV must include answerId and hasSale columns.',
      markedAnswerIds: [],
      invalidAnswerIds: [],
    };
  }

  const markedAnswerIds: number[] = [];
  const invalidAnswerIds: string[] = [];
  const seen = new Set<number>();

  for (let i = 1; i < records.length; i += 1) {
    const record = records[i];
    const hasSale = (record[hasSaleIndex] ?? '').trim();
    if (hasSale === '') {
      continue;
    }
    const rawId = (record[answerIdIndex] ?? '').trim();
    const parsedId = parseInt(rawId, 10);
    if (!rawId || !Number.isFinite(parsedId)) {
      invalidAnswerIds.push(rawId || '(blank)');
      continue;
    }
    if (!seen.has(parsedId)) {
      seen.add(parsedId);
      markedAnswerIds.push(parsedId);
    }
  }

  return { markedAnswerIds, invalidAnswerIds };
}

export function hydrateSelectedAnswers(
  answers: Array<SaleAnswerRow & { discountPercent: number }>,
): SelectedSaleAnswer[] {
  const selected: SelectedSaleAnswer[] = [];
  for (const answer of answers) {
    if (!isSelectableSaleAnswer(answer)) {
      continue;
    }
    selected.push({
      ...answer,
      key: saleAnswerKey(answer),
      answerIndex: answer.answerIndex as number,
      discountPercent: clampDiscount(answer.discountPercent),
    });
  }
  return selected;
}

export function buildSalesSavePayload(selected: SelectedSaleAnswer[]) {
  return {
    answers: selected.map((row) => ({
      episodeUuid: row.episodeUuid,
      nodeTitle: row.nodeTitle,
      answerIndex: row.answerIndex,
      discountPercent: clampDiscount(row.discountPercent),
    })),
  };
}

export function selectionFingerprint(
  selected: SelectedSaleAnswer[],
  missing: Array<{
    episodeUuid: string;
    nodeTitle: string;
    answerIndex: number | string;
    discountPercent: number;
  }> = [],
): string {
  const selectedPart = selected
    .map((row) => `${row.episodeUuid}|${row.nodeTitle}|${row.answerIndex}|${clampDiscount(row.discountPercent)}`)
    .sort()
    .join('\n');
  const missingPart = missing
    .map((row) => `missing|${row.episodeUuid}|${row.nodeTitle}|${row.answerIndex}|${clampDiscount(row.discountPercent)}`)
    .sort()
    .join('\n');
  return `${selectedPart}\n--missing--\n${missingPart}`;
}

export function formatMissingSaleAnswer(row: {
  episodeUuid: string;
  nodeTitle: string;
  answerIndex: number | string;
  discountPercent: number;
}): string {
  return `${row.episodeUuid} / ${row.nodeTitle} / answer ${row.answerIndex} (${row.discountPercent}% off)`;
}

export function matchSaleImportIds(
  answers: SaleAnswerRow[],
  markedAnswerIds: number[],
): { keysToSelect: string[]; missingIds: number[] } {
  const byAnswerId = new Map<number, SaleAnswerRow>();
  for (const answer of answers) {
    byAnswerId.set(answer.answerId, answer);
  }

  const keysToSelect: string[] = [];
  const missingIds: number[] = [];

  for (const answerId of markedAnswerIds) {
    const answer = byAnswerId.get(answerId);
    if (!answer) {
      missingIds.push(answerId);
      continue;
    }
    if (isSelectableSaleAnswer(answer)) {
      keysToSelect.push(saleAnswerKey(answer));
    }
  }

  return { keysToSelect, missingIds };
}
